import type { ReactNode } from 'react';

export interface SegTab {
  value: string;
  label: string;
  /** Optional count badge (null = hide badge). */
  count?: number | null;
  icon?: ReactNode;
}

interface Props {
  tabs: SegTab[];
  value: string;
  onChange: (v: string) => void;
  /** Accessible label for the tablist. */
  label?: string;
  className?: string;
}

/**
 * Underline tab bar with optional count badges. The single canonical idiom for
 * single-select primary filters — status pipelines, section switchers, console
 * views — replacing the competing tab styles that had drifted across modules.
 */
export default function SegmentedTabs({ tabs, value, onChange, label, className = '' }: Props) {
  return (
    <div className={`border-b border-gray-200 dark:border-gray-700 ${className}`}>
      <div role="tablist" aria-label={label} className="flex items-center gap-1 overflow-x-auto pb-px">
        {tabs.map((t) => {
          const active = value === t.value;
          return (
            <button
              key={t.value || '__all'}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => onChange(t.value)}
              className={`flex min-h-[44px] flex-shrink-0 items-center gap-1.5 whitespace-nowrap border-b-2 px-2.5 text-sm font-medium transition-colors duration-150 focus:outline-none focus-visible:bg-primary-50 dark:focus-visible:bg-primary-900/20 sm:min-h-0 sm:py-2 ${
                active
                  ? 'border-primary-600 text-primary-700 dark:text-primary-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
            >
              {t.icon}
              {t.label}
              {t.count != null && (
                <span
                  className={`rounded-full px-1.5 py-0.5 text-xs ${
                    active
                      ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300'
                      : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                  }`}
                >
                  {t.count.toLocaleString()}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
